import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";

import { INotification } from "../../../types/api/notification";
import { notificationList, updateNotificationStatus } from "./callbacks";

export const useNotificationList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<INotification[]>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponseWithQData<INotification[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<TResponseWithQData<INotification[]>, Error>({
    queryKey: [
      API_ENDPOINTS.NOTIFICATION_LIST,
      { ...queryParams, total: null },
    ],
    queryFn: async () => {
      const res = await notificationList(queryParams);
      
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    staleTime: 3000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: true,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...params }));
    },
  };
};

export const useUpdateNotificationStatus = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<INotification>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<INotification>,
    unknown,
    INotification
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await updateNotificationStatus(data);
      return response.data;
    },

    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.NOTIFICATION_LIST, { ...qData }],
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};
